import { eq, and, desc, sql } from "drizzle-orm";
import { DrizzleD1Database } from "drizzle-orm/d1";
import { documentVersions } from "../schemas/document-versions";
import { users } from "../schemas/users";
import { generateUlid, createOnlyTimestamps } from "@kubdee/shared";
import { logActivity } from "./activity.service";

// ============================================================================
// Types
// ============================================================================

export type VersionAction = "created" | "updated" | "state_changed" | "rollback" | "deleted";

export interface FieldDiff {
  old: unknown;
  new: unknown;
}

export type VersionChanges = Record<string, FieldDiff>;

export interface CreateVersionParams {
  companyId: string;
  module: string;
  doctype: string;
  documentId: string;
  snapshot: Record<string, unknown>;
  changes?: VersionChanges | null;
  action: VersionAction;
  summary?: string;
  userId: string;
}

export interface GetVersionsParams {
  module: string;
  doctype: string;
  documentId: string;
  page?: number;
  limit?: number;
}

// ============================================================================
// createVersion — Save a version snapshot (auto-increment version number)
// ============================================================================

export async function createVersion(
  db: DrizzleD1Database,
  params: CreateVersionParams
) {
  // Get next version number
  const latestVersion = await getLatestVersion(
    db,
    params.module,
    params.doctype,
    params.documentId
  );
  const nextVersion = latestVersion + 1;

  const id = generateUlid();
  const timestamps = createOnlyTimestamps(params.userId);

  await db.insert(documentVersions).values({
    id,
    companyId: params.companyId,
    module: params.module,
    doctype: params.doctype,
    documentId: params.documentId,
    version: nextVersion,
    snapshot: JSON.stringify(params.snapshot),
    changes: params.changes ? JSON.stringify(params.changes) : null,
    action: params.action,
    summary: params.summary || null,
    createdAt: timestamps.createdAt,
    createdBy: params.userId,
  });

  return { id, version: nextVersion, createdAt: timestamps.createdAt };
}

// ============================================================================
// getVersions — List all versions for a document (newest first)
// ============================================================================

export async function getVersions(
  db: DrizzleD1Database,
  params: GetVersionsParams
) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 50;
  const offset = (page - 1) * limit;

  // Get total count
  const countResult = await db
    .select({ count: sql<number>`count(*)` })
    .from(documentVersions)
    .where(
      and(
        eq(documentVersions.module, params.module),
        eq(documentVersions.doctype, params.doctype),
        eq(documentVersions.documentId, params.documentId)
      )
    )
    .get();

  const total = countResult?.count ?? 0;

  // Get versions with user info
  const versions = await db
    .select({
      id: documentVersions.id,
      companyId: documentVersions.companyId,
      module: documentVersions.module,
      doctype: documentVersions.doctype,
      documentId: documentVersions.documentId,
      version: documentVersions.version,
      snapshot: documentVersions.snapshot,
      changes: documentVersions.changes,
      action: documentVersions.action,
      summary: documentVersions.summary,
      createdAt: documentVersions.createdAt,
      createdBy: documentVersions.createdBy,
      userName: users.name,
      userEmail: users.email,
    })
    .from(documentVersions)
    .leftJoin(users, eq(documentVersions.createdBy, users.id))
    .where(
      and(
        eq(documentVersions.module, params.module),
        eq(documentVersions.doctype, params.doctype),
        eq(documentVersions.documentId, params.documentId)
      )
    )
    .orderBy(desc(documentVersions.version))
    .limit(limit)
    .offset(offset)
    .all();

  // Parse JSON fields
  const parsed = versions.map((v) => ({
    ...v,
    snapshot: v.snapshot ? JSON.parse(v.snapshot) : null,
    changes: v.changes ? JSON.parse(v.changes) : null,
  }));

  return {
    data: parsed,
    meta: {
      page,
      perPage: limit,
      total,
    },
  };
}

// ============================================================================
// getVersion — Get a specific version by version number
// ============================================================================

export async function getVersion(
  db: DrizzleD1Database,
  module: string,
  doctype: string,
  documentId: string,
  versionNum: number
) {
  const result = await db
    .select({
      id: documentVersions.id,
      companyId: documentVersions.companyId,
      module: documentVersions.module,
      doctype: documentVersions.doctype,
      documentId: documentVersions.documentId,
      version: documentVersions.version,
      snapshot: documentVersions.snapshot,
      changes: documentVersions.changes,
      action: documentVersions.action,
      summary: documentVersions.summary,
      createdAt: documentVersions.createdAt,
      createdBy: documentVersions.createdBy,
      userName: users.name,
      userEmail: users.email,
    })
    .from(documentVersions)
    .leftJoin(users, eq(documentVersions.createdBy, users.id))
    .where(
      and(
        eq(documentVersions.module, module),
        eq(documentVersions.doctype, doctype),
        eq(documentVersions.documentId, documentId),
        eq(documentVersions.version, versionNum)
      )
    )
    .get();

  if (!result) return null;

  return {
    ...result,
    snapshot: result.snapshot ? JSON.parse(result.snapshot) : null,
    changes: result.changes ? JSON.parse(result.changes) : null,
  };
}

// ============================================================================
// diffVersions — Compute field-level diff between two snapshots
// ============================================================================

const DIFF_SKIP_FIELDS = new Set([
  "id",
  "createdAt",
  "createdBy",
  "updatedAt",
  "updatedBy",
  "deletedAt",
  "deletedBy",
]);

export function diffVersions(
  v1Snapshot: Record<string, unknown>,
  v2Snapshot: Record<string, unknown>
): VersionChanges {
  const changes: VersionChanges = {};

  // Collect all keys from both snapshots
  const allKeys = new Set([
    ...Object.keys(v1Snapshot),
    ...Object.keys(v2Snapshot),
  ]);

  for (const key of allKeys) {
    if (DIFF_SKIP_FIELDS.has(key)) continue;

    const oldVal = v1Snapshot[key] ?? null;
    const newVal = v2Snapshot[key] ?? null;

    // Compare as JSON strings to handle objects/arrays
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes[key] = { old: oldVal, new: newVal };
    }
  }

  return changes;
}

// ============================================================================
// rollback — Restore document to a specific version
// ============================================================================

export async function rollback(
  db: DrizzleD1Database,
  module: string,
  doctype: string,
  documentId: string,
  versionNum: number,
  opts: {
    companyId: string;
    userId: string;
    /** Callback to actually update the document record with the snapshot */
    updateDocument: (
      db: DrizzleD1Database,
      snapshot: Record<string, unknown>
    ) => Promise<void>;
  }
) {
  // 1. Load the target version snapshot
  const targetVersion = await getVersion(
    db,
    module,
    doctype,
    documentId,
    versionNum
  );

  if (!targetVersion) {
    throw new Error(`Version ${versionNum} not found`);
  }

  // 2. Get the current latest version to compute diff
  const latestVersionNum = await getLatestVersion(db, module, doctype, documentId);
  const latestVersion = await getVersion(
    db,
    module,
    doctype,
    documentId,
    latestVersionNum
  );

  // 3. Submitted/approved documents must use amend/reversal semantics.
  assertRollbackAllowed(latestVersion?.snapshot ?? targetVersion.snapshot);

  // 4. Update the document record using the callback
  await opts.updateDocument(db, targetVersion.snapshot);

  // 5. Compute diff between current and target for the rollback version
  const changes = latestVersion
    ? diffVersions(latestVersion.snapshot, targetVersion.snapshot)
    : null;

  // 6. Create a new version recording the rollback
  const rollbackVersion = await createVersion(db, {
    companyId: opts.companyId,
    module,
    doctype,
    documentId,
    snapshot: targetVersion.snapshot,
    changes,
    action: "rollback",
    summary: `ย้อนกลับเป็นเวอร์ชัน ${versionNum}`,
    userId: opts.userId,
  });

  // 7. Log activity
  await logActivity(db, {
    module,
    doctype,
    document_id: documentId,
    action: "updated",
    summary: `ย้อนกลับเป็นเวอร์ชัน ${versionNum}`,
    user_id: opts.userId,
  });

  return rollbackVersion;
}

export function assertRollbackAllowed(snapshot: Record<string, unknown>): void {
  if (isSubmittedLikeSnapshot(snapshot)) {
    throw new Error(
      "AMENDMENT_REQUIRED: เอกสารที่ submit/approve แล้วต้องใช้การ amend หรือ reversal แทน rollback",
    );
  }
}

function isSubmittedLikeSnapshot(snapshot: Record<string, unknown>): boolean {
  const rawDocStatus = snapshot.docStatus ?? snapshot.doc_status;
  const docStatus =
    typeof rawDocStatus === "number"
      ? rawDocStatus
      : typeof rawDocStatus === "string" && rawDocStatus.trim() !== ""
        ? Number(rawDocStatus)
        : null;
  if (docStatus !== null && Number.isFinite(docStatus) && docStatus >= 1) return true;

  const rawStatus = snapshot.status ?? snapshot.workflowState ?? snapshot.workflow_state ?? snapshot.state;
  if (typeof rawStatus !== "string") return false;
  return [
    "submitted",
    "approved",
    "paid",
    "closed",
    "completed",
    "cancelled",
    "void",
  ].includes(rawStatus.toLowerCase());
}

// ============================================================================
// getLatestVersion — Get the latest version number for a document
// ============================================================================

export async function getLatestVersion(
  db: DrizzleD1Database,
  module: string,
  doctype: string,
  documentId: string
): Promise<number> {
  const result = await db
    .select({ maxVersion: sql<number>`COALESCE(MAX(${documentVersions.version}), 0)` })
    .from(documentVersions)
    .where(
      and(
        eq(documentVersions.module, module),
        eq(documentVersions.doctype, doctype),
        eq(documentVersions.documentId, documentId)
      )
    )
    .get();

  return result?.maxVersion ?? 0;
}
